import * as Sentry from '@sentry/react';
import { generators, macroRunReplySchema, macroRunCommandSchema, type MacroExecutionBundle, type MacroRunCommand,
  type MacroRunReply, type MacroRuntimeMode, type PlaybackAssetGrant, type PlaycastMessageProcessMacroRun,
  type PlaycastMessage, type MacroLiveDetection } from '@playcastdotio/Messaging';
import { NativeMacroTelemetry } from './NativeMacroTelemetry';

interface Pending {
  command: MacroRunCommand;
  resolve(reply: MacroRunReply): void;
  timer: ReturnType<typeof setTimeout>;
}
/**
 * How long a native `start` may run before the bridge fails it as
 * `native_reply_timeout` and requests cancellation. Covers the full launch
 * tree (every node's wait deadline) plus transit.
 */
export const NATIVE_MACRO_START_TIMEOUT_MS = 3_645_000;
const CANCEL_REPLY_TIMEOUT_MS = 45_000;
const CAPABILITIES_REPLY_TIMEOUT_MS = 1_500;
const STATUS_REPLY_TIMEOUT_MS = 5_000;

function bundleOf(command: MacroRunCommand): MacroExecutionBundle | undefined {
  return command.command === 'start' ? command.bundle : undefined;
}

type NativeMacroSidecar = { notify(input: unknown): boolean };

/** Correlated native requests. A lost start reply never permits replay. */
export class NativeMacroBridge {
  private readonly pending = new Map<string, Pending>();
  private readonly sidecars: readonly NativeMacroSidecar[];
  public constructor(private readonly send: (message: PlaycastMessage<PlaycastMessageProcessMacroRun>) => void,
    sidecars: readonly NativeMacroSidecar[] | NativeMacroSidecar = []) {
    // A single sidecar remains accepted so existing analysis wiring and tests keep working.
    this.sidecars = Array.isArray(sidecars) ? sidecars : [sidecars];
  }

  public capabilities(): Promise<MacroRunReply> {
    return this.request({ command: 'capabilities', requestId: crypto.randomUUID(), runId: '' }, CAPABILITIES_REPLY_TIMEOUT_MS);
  }
  /** Starts the bundle; `assets` is the grant Playjector uses to fetch the bundle's reference pages. */
  public start(runId: string, bundle: MacroExecutionBundle, mode: MacroRuntimeMode, assets: PlaybackAssetGrant): Promise<MacroRunReply> {
    return this.request({ command: 'start', requestId: crypto.randomUUID(), runId, bundle, mode, assets }, NATIVE_MACRO_START_TIMEOUT_MS);
  }
  /**
   * Observation-only Live Detection: Playjector scores every captured frame of the capture-UI-selected
   * source against the bundle's checkpoints and reports them through `status`. No process, no input.
   */
  public startLiveDetection(runId: string, bundle: MacroExecutionBundle, assets: PlaybackAssetGrant,
    liveDetection: MacroLiveDetection): Promise<MacroRunReply> {
    return this.request({ command: 'start', requestId: crypto.randomUUID(), runId, bundle, mode: 'local-shadow', assets, liveDetection },
      NATIVE_MACRO_START_TIMEOUT_MS);
  }
  /** Returns Playjector's current bounded snapshot for an existing run. */
  public status(runId: string): Promise<MacroRunReply> {
    return this.request({ command: 'status', requestId: crypto.randomUUID(), runId }, STATUS_REPLY_TIMEOUT_MS);
  }
  public setPreviewSensitivity(runId: string, sensitivity: number, rawScores: boolean): Promise<MacroRunReply> {
    return this.request({ command: 'preview-sensitivity', requestId: crypto.randomUUID(), runId, sensitivity, rawScores }, STATUS_REPLY_TIMEOUT_MS);
  }
  public async cancel(runId: string): Promise<boolean> {
    if (!runId) return true;
    const acknowledgement = await this.request({ command: 'cancel', requestId: crypto.randomUUID(), runId }, CANCEL_REPLY_TIMEOUT_MS);
    const confirmed = acknowledgement.outcome === 'cancelled';
    for (const [id, pending] of this.pending) {
      if (pending.command.command !== 'start' || pending.command.runId !== runId) continue;
      this.finish({ requestId: id, runId, revision: pending.command.bundle.revision,
        outcome: confirmed ? 'cancelled' : 'failed', inputsSent: true,
        errorCode: confirmed ? 'host_cancelled' : 'input_release_unconfirmed' });
    }
    return confirmed;
  }
  public notify(input: unknown): void {
    for (const sidecar of this.sidecars) if (sidecar.notify(input)) return;
    const parsed = macroRunReplySchema.safeParse(input);
    if (!parsed.success) return;
    const reply = parsed.data, pending = this.pending.get(reply.requestId);
    if (!pending || pending.command.runId !== reply.runId) return;
    const bundle = bundleOf(pending.command);
    if (bundle && bundle.revision !== reply.revision) return;
    // The start request owns only the terminal reply. A status request, by
    // contrast, exists specifically to receive the current running snapshot.
    if (reply.outcome === 'running' && pending.command.command === 'start') return;
    this.finish(reply);
  }
  private finish(reply: MacroRunReply): void {
    const pending = this.pending.get(reply.requestId);
    if (!pending) return;
    clearTimeout(pending.timer); this.pending.delete(reply.requestId);
    this.logTerminal(pending.command, reply);
    pending.resolve(reply);
  }
  private logTerminal(command: MacroRunCommand, reply: MacroRunReply): void {
    try {
      if (!Sentry.isInitialized()) return;
      const level = reply.outcome === 'failed' || (command.command === 'cancel' && reply.outcome !== 'cancelled') ? 'error' : 'info';
      Sentry.logger[level]('native macro request completed', NativeMacroTelemetry.diagnostic(command, reply));
    } catch { /* Telemetry cannot interfere with request completion or cleanup. */ }
  }
  private request(command: MacroRunCommand, timeoutMs: number): Promise<MacroRunReply> {
    const revision = bundleOf(command)?.revision ?? '';
    if (!macroRunCommandSchema.safeParse(command).success) {
      const reply: MacroRunReply = { requestId: command.requestId, runId: command.runId,
        revision, outcome: 'failed', inputsSent: false, errorCode: 'invalid_command' };
      this.logTerminal(command, reply);
      return Promise.resolve(reply);
    }
    return new Promise(resolve => {
      const fail = (errorCode = 'native_reply_timeout') => {
        this.finish({ requestId: command.requestId, runId: command.runId, revision, outcome: 'failed',
          inputsSent: command.command === 'start', errorCode });
        if (command.command === 'start') void this.cancel(command.runId);
      };
      const timer = setTimeout(fail, timeoutMs);
      this.pending.set(command.requestId, { command, resolve, timer });
      try { this.send(generators.process.macroRun(command)); }
      catch { clearTimeout(timer); fail('native_send_failed'); }
    });
  }
}
